import { Injectable } from '@angular/core';
import { WebAuth } from 'auth0-js';
import { sessionStorageKey } from 'src/app/doctors-station/doctors-station.constants';
import { DxauthService } from 'src/app/dxauth.service';
import { environment } from 'src/environments/environment';
import {
  AppType,
  Products,
  productsPerType,
} from 'src/environments/environmentTypes';
import { baseUrl, errorDescriptionsMap, extractRole } from '../utils';
import { cleanAuth0Cookies } from '../utils/cookie-utils';
import { Auth0SignupResult } from './auth0.types';

const connection = 'Username-Password-Authentication';
type LoginParams = {
  clientId: string;
  email: string;
  password: string;
  redirectUri: string;
  responseType: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  connection?: string;
  options: {
    errorDescripitionsMap: typeof errorDescriptionsMap;
  };
};

@Injectable({
  providedIn: 'root',
})
export class Auth0Service {
  constructor(public dxauth: DxauthService) {}

  async checkLogin(params: {
    connection?: string;
    redirectUri: string;
    scope?: string;
  }) {
    const auth = new WebAuth({
      // セッション確認時にaccessTokenを取得した場合、その時点でgrantsに追加されてしまうため、セッション確認用は仮のclient_idを共通で利用する
      clientID: environment.tmpClientId,
      domain: environment.domain,
    });
    const result = await new Promise<{
      withError: boolean;
      accessToken?: string;
    }>((resolve, reject) => {
      auth.checkSession(
        {
          responseType: 'token',
          redirectUri: `https://${location.host}`, // セッション確認の際のリダイレクトURLはホスト部分固定
          audience: environment.audience,
          connection: params.connection ?? connection,
          scope: params.scope, // 指定があった場合はscopeも指定する
        },
        (error, response) => {
          // console.error(error);
          if (error) {
            reject(error);
            return;
          }
          resolve({ ...response, withError: false });
        }
      );
    });
    return result;
  }

  authorize(params: {
    clientId: string;
    state: string;
    nonce?: string;
    redirectUri: string;
    responseType: string;
    responseMode: string;
    scope: string;
    code_challenge?: string;
    code_challenge_method?: 'S256';
  }) {
    const auth = new WebAuth({
      clientID: params.clientId,
      domain: environment.domain,
    });
    auth.authorize({
      redirectUri: params.redirectUri,
      state: params.state,
      responseType: params.responseType,
      nonce: params.nonce,
      scope: params.scope,
      responseMode: params.responseMode,
      audience: environment.audience,
    });
  }

  async register(params: {
    clientId: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    gender?: 'male' | 'female';
    birthDate?: string;
    defaultRole?: string;
    connection?: string;
    profile?: string;
  }) {
    params.connection = params.connection || connection;
    const auth = new WebAuth({
      domain: environment.domain,
      clientID: params.clientId,
    });
    // auth.redirect.loginWithCredentials
    return new Promise<Auth0SignupResult>((resolve, reject) => {
      auth.signup(
        {
          connection: params.connection,
          email: params.email,
          password: params.password,
          userMetadata: {
            given_name: params.firstName,
            family_name: params.lastName,
            gender: params.gender,
            birthdate: params.birthDate,
            role: params.defaultRole,
            profile: params.profile,
          },
        } as any,
        (error, result) => {
          result = result as Auth0SignupResult;
          if (error) {
            reject(error);
          }
          resolve(result);
        }
      );
    });
  }

  // チェック込みの初回ログイン処理
  loginFirst(params: LoginParams) {
    return this.login(
      {
        ...params,
        redirectUri: this.baseRedirectUri(params.redirectUri, params.clientId),
      },
      params.options.errorDescripitionsMap
    );
  }
  // チェック込みの初回ログイン処理
  loginSecond(params: LoginParams) {
    return this.login(
      {
        ...params,
        redirectUri: params.redirectUri,
      },
      params.options.errorDescripitionsMap
    );
  }

  async login(
    params: LoginParams,
    errorDescripitionsMap: typeof errorDescriptionsMap
  ) {
    // カスタムのコネクションが指定されている場合はそれを使う
    params.connection = params.connection || connection;
    // https://doctors-jp.backlog.com/view/DXUT-63
    // アプリケーションに未ログインの場合はアカウント連携ページへ遷移する
    await this.setAccountIntegrationEnableFlag(params);
    const auth = new WebAuth({
      domain: environment.domain,
      clientID: params.clientId,
    });

    type LoginResponse = {
      login_ticket: string;
      co_verifier: string;
      co_id: string;
    };
    await new Promise<LoginResponse>((resolve, reject) => {
      auth.login(
        {
          email: params.email,
          password: params.password,
          redirectUri: params.redirectUri,
          responseType: params.responseType,
          realm: params.connection,
        },
        (error, result: LoginResponse) => {
          if (error) {
            return reject(
              (error.description = errorDescripitionsMap[error?.description])
            );
          }
          resolve(result);
        }
      );
    });
  }

  async isApplicationLogined(params: {
    email?: string;
    accessToken?: string;
    clientId: string;
  }) {
    try {
      // accessTokenを使ってログインしている場合は、アプリケーションにログインしているかどうかを判定する
      if (params.accessToken) {
        return this.dxauth.isLoginedToApplicationByAccessToken({
          accessToken: params.accessToken,
          clientId: params.clientId,
        });
      }
      if (params.email) {
        // emailを使ってログインしている場合は、アプリケーションにログインしているかどうかを判定する
        return this.dxauth.isLoginedToApplication({
          email: params.email,
          clientId: params.clientId,
        });
      }
    } catch (error) {
      return { is_logined: false };
    }
    throw new Error('email or accessToken is required');
  }

  static isAccountIntegrationTarget(params: { clientId: string }) {
    const appConfig = environment.apps[params.clientId];
    // 明示的に有効化されているもののみ有効化
    if (appConfig.isEnableAccountIntegration) {
      return true;
    }
    return false;

    // 最終的な処理の想定は以下. typeから判定する
    // const appType = environment.apps[params.clientId]?.type;
    // const productType = productsPerType[appType];
    // // cp_productsの場合は連携機能の対象外
    // if (productType?.[0] === 'cp_products') return false;
    // return true;
  }

  /**
   * アプリケーションに未ログインの場合はアカウント連携ページへ遷移する. 必要な設定をsessionStorageに設定する
   * @link https://doctors-jp.backlog.com/view/DXUT-63
   * @param params
   */
  private async setAccountIntegrationEnableFlag(params: {
    email?: string;
    accessToken?: string;
    clientId: string;
  }) {
    // 対象外の場合は何もしない
    if (
      !Auth0Service.isAccountIntegrationTarget({
        clientId: params.clientId,
      })
    ) {
      return;
    }
    environment.apps[params.clientId]?.type;
    try {
      const isApplicationLogined = await this.isApplicationLogined(params);
      if (!isApplicationLogined.is_logined) {
        // 未ログインの場合は仮のclient_idにログインさせる形に変更してログイン処理を行う
        sessionStorage.setItem(
          sessionStorageKey.isAccountIntegrationClientId,
          `${params.clientId}`
        );
        params.clientId = environment.tmpClientId;
      }
    } catch (error) {
      // 基本的に通らないルート
      alert('invalid state');
    }
  }

  changePassword(params: { clientId: string; email: string }) {
    const auth = new WebAuth({
      domain: environment.domain,
      clientID: params.clientId,
    });
    auth.changePassword(
      {
        connection: 'Username-Password-Authentication',
        email: params.email,
      },
      (e) => {}
    );
  }

  /** プロダクトのフラグを判定して、自動ログイン可能か否かを返す */
  static isAutoLoginable(params: {
    products: Products[];
    flags: Record<Products, boolean>;
  }) {
    // アプリ側未設定の場合はtrue(自動ログイン可能)
    if (params.products.length === 0) return true;

    for (const flag of params.products) {
      if (params.flags[flag]) return true;
    }

    // それ以外はページを表示する
    return false;
  }

  static getProductsByType(type: AppType): Products[] {
    const product = productsPerType[type];
    // 未指定の場合は空
    if (!product) return ['doctors_products'];

    return product;
  }

  async isLogined(params: {
    clientId: string;
    redirectUri: string;
    accessToken?: string;
    email?: string;
  }): Promise<boolean> {
    // アプリtypeが未設定の場合はfalse. 異常パターン
    const appType = environment.apps[params.clientId]?.type;
    if (!appType) {
      return false;
    }

    // フラグを取得
    const flags = await this.getFlags(params);
    // フラグを判定して自動ログイン可能か否かを返す
    const isLoginable = Auth0Service.isAutoLoginable({
      products: Auth0Service.getProductsByType(appType),
      flags,
    });
    // ログイン可能な場合はtrue
    if (isLoginable) {
      return true;
    }
    // ログイン不可の場合はfalse
    return false;
  }

  async getFlags(params: {
    clientId: string;
    redirectUri: string;
    accessToken?: string;
    email?: string;
  }) {
    try {
      if (params.accessToken) {
        return await this.dxauth.geIntegratedFlagByAccessToken(
          params.accessToken
        );
      }
      if (params.email) {
        return await this.dxauth.geIntegratedFlagByEmail(params.email);
      }
      return { cp_products: false, doctors_products: false };
    } catch (error) {
      return {
        cp_products: false,
        doctors_products: false,
      };
    }
  }

  get lastLogin() {
    return new Date();
  }

  /**
   * LINE SSOログインを行う.
   * location.hrefを変更してリダイレクトが行われるため、何か処理をしておく必要がある場合はこの処理の前に行う必要がある
   */
  async loginByLineSSO() {
    // Auth0管理のログインURLに遷移する
    // パラメータはそのまま渡す. connection=LINEを明示的に指定する必要があるため付与する
    location.href = `https://${environment.domain}/authorize/${location.search}&connection=LINE`;
  }

  /** 既にログイン済みの場合はログインさせる */
  async autoLoginWithSession(params: {
    clientId: string;
    state: string;
    nonce: string;
    responseMode: string;
    responseType: string;
    scope: string;
    redirectUri: string;
    codeChallenge?: string;
    codeChallengeMethod?: 'S256';
  }): Promise<boolean> {
    try {
      // 前回SSO時に残っているクッキーを削除する
      cleanAuth0Cookies();
      const result: {
        withError: boolean;
        accessToken?: string;
      } = await this.checkLogin({
        redirectUri: params.redirectUri,
        connection: environment.apps[params.clientId]?.connection,
      }).catch((error) => {
        // consent requiredの場合は処理の続きをしないといけないのでそのまま返す
        if (error.code === 'consent_required') return { withError: false };
        if (error.description === 'Multifactor authentication required')
          return { withError: false };
        // const managedErrors = ['verify_email', 'invalid_role'];
        const managedErrors = ['invalid_role'];
        // Auth0側ではなく、ID基盤として定義したエラーはここでは成功として扱う
        if (managedErrors.includes(error.description)) {
          return { withError: true };
        }
        throw error;
      });
      if (!result) {
        return false;
      }

      // アプリのタイプ種別が'lasuite'の場合は、自動ログイン処理をして良いものとする
      // 20230523暫定対応漏れ. 20250327追加対応
      const isLasuite =
        environment.apps[params.clientId]?.connection === 'DST-OEM';
      const isDxSales =
        environment.apps[params.clientId]?.connection === 'CBPT-DxSales';
      const isSokusaiOem =
        environment.apps[params.clientId]?.connection === 'DST-SOKUSAI-OEM';
      const isMr = environment.apps[params.clientId]?.connection === 'CBPT-MR';
      const isHealthpicks =
        environment.apps[params.clientId]?.connection === 'DST-HEALTHPICKS';
      // 別コネクションの場合は自動ログインをスキップする
      const isProductionFlagSkip =
        isLasuite || isDxSales || isSokusaiOem || isMr || isHealthpicks;

      const isAutoLogin =
        isProductionFlagSkip ||
        (await this.isLogined({
          clientId: params.clientId,
          redirectUri: params.redirectUri,
          accessToken: result.accessToken,
        }));
      if (!isAutoLogin) {
        return false;
      }
      // 自動ログインが走る条件を満たしているが、まだログインしていない場合はアカウント連携ページへ遷移する
      if (
        await this.setAccountIntegrationEnableFlagOnAutoLogin(params, result)
      ) {
        return;
      }
      // ログイン済み状態の場合は認証処理実施してログイン状態とする
      await this.authorize({
        clientId: params.clientId,
        state: params.state,
        nonce: params.nonce,
        redirectUri: result.withError
          ? this.baseRedirectUri(params.redirectUri, params.clientId)
          : params.redirectUri,
        responseMode: params.responseMode,
        responseType: params.responseType,
        scope: params.scope,
        code_challenge: params.codeChallenge,
        code_challenge_method: params.codeChallengeMethod,
      });
      return true;
    } catch (error) {
      // console.error(error);
      return false;
    }
  }

  private async setAccountIntegrationEnableFlagOnAutoLogin(
    queryParams: {
      clientId: string;
      state: string;
      nonce: string;
      responseMode: string;
      responseType: string;
      scope: string;
      redirectUri: string;
      codeChallenge?: string;
      codeChallengeMethod?: 'S256';
    },
    result: { accessToken?: string }
  ) {
    // 対象外の場合は何もしない
    if (
      !Auth0Service.isAccountIntegrationTarget({
        clientId: queryParams.clientId,
      })
    ) {
      return;
    }

    const isApplicationLogined = await this.isApplicationLogined({
      clientId: queryParams.clientId,
      accessToken: result.accessToken,
    });

    if (!isApplicationLogined.is_logined) {
      // ログイン時の全く同じ処理を行うため、sessionStorageにクエリを保存しておく
      sessionStorage.setItem(
        environment.sessionStorageKey,
        JSON.stringify({
          client_id: queryParams.clientId,
          state: queryParams.state,
          redirect_uri: queryParams.redirectUri,
          response_type: queryParams.responseType,
          scope: queryParams.scope,
          nonce: queryParams.nonce,
          code_challenge: queryParams.codeChallenge,
          code_challenge_method: queryParams.codeChallengeMethod,
        })
      );
      // この状態でアカウント連携ページへ遷移する
      location.pathname = 'account-integration';
      return true;
    }
    return false;
  }

  baseRedirectUri(redirectUri: string, clientId: string) {
    return baseUrl(clientId) + `?${extractRole(redirectUri)}`;
  }

  async sendPasswordReset(params: {
    email: string;
    clientId: string;
    connection?: string;
  }) {
    params.connection = params.connection || connection;
    const auth = new WebAuth({
      domain: environment.domain,
      clientID: params.clientId,
    });

    return await new Promise((resolve, reject) => {
      auth.changePassword(
        {
          connection: params.connection,
          email: params.email,
        },
        (error, result) => {
          if (error) {
            return reject(error);
          }
          return resolve(result);
        }
      );
    });
  }

  logout(clientId: string, redirectUri: string) {
    const auth = new WebAuth({
      domain: environment.domain,
      clientID: clientId,
    });

    auth.logout({
      returnTo: redirectUri,
    });
  }
}
